import { memo, ReactNode } from 'react';

import clsx from 'clsx';
import { HashLink } from 'react-router-hash-link';

import { TConfig } from '@@/auto/interfaces';
import Link from '@@/common/components/Link';

import classes from './Footer.module.css';

export type TFooter = {
  cmsContent?: ReactNode;
  hasNavItems: boolean;
  hasPersonalInfoLink: boolean;
  header?: ReactNode;
  overrideClasses?: { root?: string; container?: string; mainWrapper?: string };
  phoneLink?: ReactNode;
  sureFooterLogoSrc?: string;
  tenantPhoneVariant?: TConfig['footerConfig']['tenantPhoneVariant'];
  getSocialLinks?: TConfig['footerConfig']['getSocialLinks'];
};

const contactHref = process.env.REACT_APP_CONTACT_URL;
const claimsHref = process.env.REACT_APP_CLAIMS_URL;
const blogHref = process.env.REACT_APP_BLOG_URL || '/';
const aboutUsHref = process.env.REACT_APP_ABOUT_US_URL!;

function Footer({
  cmsContent = null,
  header = null,
  hasNavItems,
  hasPersonalInfoLink,
  getSocialLinks,
  overrideClasses = {},
  phoneLink = null,
  sureFooterLogoSrc,
  tenantPhoneVariant,
}: TFooter) {
  return (
    <footer
      className={clsx(classes.root, overrideClasses.root)}
      data-tenant-phone-variant={tenantPhoneVariant}
      data-testid="footer"
      id="footer"
    >
      <div className={clsx(classes.container, overrideClasses.container)}>
        <div className={clsx(classes.mainWrapper, overrideClasses.mainWrapper)}>
          {header}
          <div className={classes.links}>
            {hasNavItems && (
              <ul className={classes.linksList} data-testid="footer-links">
                {contactHref && (
                  <li
                    className={classes.linkItem}
                    data-testid="footer-link-contact"
                  >
                    <a
                      className={classes.linkItem_link}
                      href={contactHref}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Contact
                    </a>
                  </li>
                )}
                {aboutUsHref && (
                  <li
                    className={classes.linkItem}
                    data-testid="footer-link-about-us"
                  >
                    <Link className={classes.linkItem_link} to={aboutUsHref}>
                      About us
                    </Link>
                  </li>
                )}
                {claimsHref && (
                  <li
                    className={classes.linkItem}
                    data-testid="footer-link-claims"
                  >
                    <a
                      className={classes.linkItem_link}
                      href={claimsHref}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Claims
                    </a>
                  </li>
                )}
                <li
                  className={classes.linkItem}
                  data-testid="footer-link-terms"
                >
                  <Link className={classes.linkItem_link} to="/terms">
                    Terms
                  </Link>
                </li>
                <li
                  className={classes.linkItem}
                  data-testid="footer-link-privacy"
                >
                  <Link className={classes.linkItem_link} to="/privacy">
                    Privacy
                  </Link>
                </li>
                {blogHref && (
                  <li
                    className={classes.linkItem}
                    data-testid="footer-link-blog"
                  >
                    <a
                      className={classes.linkItem_link}
                      href={blogHref}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Blog
                    </a>
                  </li>
                )}
              </ul>
            )}

            <div className={classes.topWrapper}>
              {tenantPhoneVariant === 'first-row' && phoneLink}
              {getSocialLinks && !!getSocialLinks().length && (
                <ul
                  className={classes.socialList}
                  data-testid="footer-social-links"
                >
                  {getSocialLinks().map((socialLink) => (
                    <li key={socialLink.label} className={classes.socialItem}>
                      <a
                        aria-label={socialLink.label}
                        className={classes.socialItem_link}
                        href={socialLink.href}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <i className={clsx('fab', socialLink.fabIcon)} />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className={classes.info}>
            <div className={classes.wrapper}>
              <div className={classes.innerWrapper}>
                {tenantPhoneVariant === 'second-row' && phoneLink}

                {!!sureFooterLogoSrc && (
                  <div
                    className={classes.sureBlock}
                    data-testid="footer-sure-block"
                  >
                    <a
                      className={classes.sureBLock_link}
                      href="https://www.sureapp.com"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <img
                        alt="Built on Sure logo"
                        className={classes.sureBLock_link_img}
                        src={sureFooterLogoSrc}
                      />
                    </a>
                  </div>
                )}
              </div>
              {hasPersonalInfoLink && (
                <HashLink
                  className={classes.personalInformation_link}
                  data-testid="footer-personal-information"
                  to="/privacy#do-not-sell-my-personal-information"
                >
                  Do not sell my personal information{' '}
                  <i
                    className={clsx(classes.chevron, 'fas', 'fa-chevron-right')}
                  />
                </HashLink>
              )}
            </div>
            {cmsContent}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default memo(Footer);
